import useMsTeamsClient from 'gcs-common/hooks/useMsTeamsClient';
import changeSelectedChannelId from 'gcs-common/slices/channels/channelsThunks/changeSelectedChannelId';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

/**
 * Handles the routing to the correct target channel, when the user
 * clicks on a notification for a new message in MS Teams.
 *
 * Clicking on the notification opens the deeplink to BEEM.
 * This hook handles routing to the correct channel when the deeplink is clicked.
 */

// manually typed, check in backend
type ContextSubEntity = {
  channelId?: string;
  ticketId?: string;
  userId?: string;
};

const useMsTeamsNotificationRouting = () => {
  const { isInitialized, getContext } = useMsTeamsClient();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    // workaround to pass data to BEEM from the notification:
    // the only way to pass additional information to the application with the deeplink
    // is to the use the 'subEntityId' context argument which populates context.page.subPageId
    // see the API where we construct the deeplink and also this documentation
    // https://learn.microsoft.com/en-us/microsoftteams/platform/concepts/build-and-test/deep-link-application
    const notificationRouting = async () => {
      try {
        const context = await getContext();
        if (!context) {
          return;
        }

        const rawContextSubEntity = context.page.subPageId;
        if (!rawContextSubEntity) {
          return;
        }

        let contextSubEntity: ContextSubEntity;

        if (typeof rawContextSubEntity === 'string') {
          const decodedSubEntityId = decodeURIComponent(rawContextSubEntity);
          contextSubEntity = JSON.parse(decodedSubEntityId);
        } else {
          contextSubEntity = rawContextSubEntity as ContextSubEntity;
        }

        const { channelId, ticketId, userId } = contextSubEntity;

        if (channelId) {
          return dispatch(changeSelectedChannelId({ selectedChannelId: channelId }));
        }

        if (ticketId) {
          navigate(`/ticket-dashboard?creatorIds=${userId}&ticketId=${ticketId}`);
        }
      } catch (error) {
        console.error('Error in notification routing:', error);
      }
    };

    notificationRouting();
  }, [isInitialized, getContext, dispatch, navigate]);
};

export default useMsTeamsNotificationRouting;
